import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { ActivatedRoute } from '@angular/router';
import { AlertService } from 'src/app/services/alert.service';
import { ApiService } from 'src/app/services/api.service';
import { FileUploadModalService } from 'src/app/services/file-upload-modal.service';
import { RetryFileUploadComponent } from '../retry-file-upload/retry-file-upload.component';
import { IpcChannel, IpcV2Service } from 'src/app/services/ipc-v2.service';
import { environment } from 'src/environments/environment';

export enum Status {
  Completed = 'Completed',
  InProcess = 'In-Process',
  Remove = 'Remove',
  VerificationRequired = 'Verification Required',
  Analyzing = 'Analyzing',
  OcrProcessing = 'OCR Processing',
  FilingDocuments = 'Filing Documents',
  InformationRequired = 'Information Required',
}

@Component({
  selector: 'app-uploads',
  templateUrl: './uploads.component.html',
  styleUrls: ['./uploads.component.scss']
})
export class UploadsComponent implements OnInit {
  isLoading = true;

  @ViewChild('inputFile') inputFile: ElementRef;

  fileStatus = [
    {
      category: 'In-Process',
      icon : 'fa-solid fa-rotate text-primary',
      status: ['In-Process', 'Analyzing', 'OCR Processing', 'Filing Documents', 'Conversion Queued', 'Conversion Completed', 'Conversion Complete'],
    },
    {
      category: 'Completed',
      icon : 'fa-solid fa-circle-check text-success',
      status: ['Completed'],
    },
    {
      category: 'Removed',
      icon : 'fa-solid fa-circle-minus text-secondary',
      status: ['Removed'],
    },
    {
      category: 'User Action Required',
      icon : 'fa-solid fa-circle-exclamation text-warning',
      status: ['Verification Required', 'Information Required'],
    },
    {
      category: 'Error',
      icon : 'fa-solid fa-octagon-exclamation text-danger',
      status: ['Error'],
    },
  ];

  mapping: any = {
    sr: 'TOOL_DROPLET_SR',
    ar: 'TOOL_DROPLET_AR',
    sped: 'TOOL_DROPLET_SPED',
    er: 'TOOL_DROPLET_ER',
    all: 'TOOL_VIRTUAL_PRINTER'
  };

  colorMapping:any = {
    sr: 'warning',
    ar: 'info',
    sped: 'primary',
    er: 'success',
  }

  type: string = 'sr';

  constructor(
    private readonly api: ApiService, 
    private readonly dialog: MatDialog, 
    private readonly route: ActivatedRoute, 
    private readonly as: AlertService, 
    public fileUploadModalService:FileUploadModalService,
    private readonly ipcV2: IpcV2Service) { 
    fileUploadModalService.modalObserver.asObservable().subscribe(_=>{
      this.ngOnInit();
    })
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.route.url.subscribe(res=>{
        this.type = (""+res[0].path).split("_")[1];
      })
      // this.type = params['type'];
      this.isLoading = true;
      this.api.getRecentUploads(this.mapping[this.type]).subscribe(
        data => {
          this.data = data as any[];
          this.isLoading = false;

        // for testing put retry flag on some records
        // this.data.forEach((item, index) => {
        //   if (index % 5 === 0) {
        //     item.retry = true;
        //   }
        // });

        this.data.sort((a, b) => {
          if (a.retry === true && b.retry !== true) return -1;
          if (a.retry !== true && b.retry === true) return 1;
          return 0;
        });

        this.pagination.total = this.data.length;
      }, (err:HttpErrorResponse) => {
        if(err.status == 401) this.as.toast("You are not authorized to view this page");
        else if(err.status == 404) this.as.toast("No files found");
        else if(err.status == 500) this.as.toast("Internal server error");
        else if(err.status == 0) this.as.toast("No internet OR VPN issue");
        else this.as.toast(err.statusText);
        this.isLoading = false;
      });
    });
  }

  data: any[] = [];
  pageEvent!: PageEvent;
  pagination = { offset: 0, limit: 15, reverse: -1, total: 15 };
  pdf: any;
  pdfDownloading: any = {};
  pdfLoading: any = {};
  pdfWin!:Window;


  viewFile(name: string, link: string) {
    this.pdfLoading[name] = true;
    this.api.downloadFileBlob(link).subscribe(data => {
      console.log(data);
      let file = new Blob([data], { type: 'application/pdf' });
      let fileURL = URL.createObjectURL(file);
      this.pdfLoading[name] = false;
      const newWindow:any = window.open();
      newWindow.document.write(`<title>Pdf Reader</title><iframe src="${fileURL}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`)
    });
  }

  downloadFile(id: string, name: string, link: string) {
    this.pdfDownloading[id] = true;
    this.api.downloadFileBlob(link).subscribe(data => {
      let file = new Blob([data], { type: 'application/pdf' });
      let fileURL = URL.createObjectURL(file);

      if (!name.includes('.')) {
        name += '.pdf'; // Append default extension if none exists
      }      

      // create link to download and click on it
      let link = document.createElement('a');
      link.href = fileURL;
      link.download = name;
      link.click();
      this.pdfDownloading[id] = false;
    });
  }

  pageChange(event: PageEvent) {
    this.pagination.limit = event.pageSize;
    this.pagination.offset = event.pageSize * event.pageIndex;
  }

  uploadFiles(event: any) {
    let fl = event.target.files;
    if (!fl || fl.length == 0) return;

    this.fileUploadModalService.filesDropped(fl);
    this.inputFile.nativeElement.value = '';
  }

  retryUpload(file ){
    let dialogue = this.dialog.open(RetryFileUploadComponent, {
      width: '70vw',
      maxHeight: '90vh',
      data: file
    });
    dialogue.afterClosed().subscribe(_ => { this.ngOnInit(); this.inputFile.nativeElement.value = ''; });
    dialogue.backdropClick().subscribe(_ => { this.ngOnInit(); this.inputFile.nativeElement.value = ''; });
  }

  isPDF(name: string) {
    return name.endsWith('.pdf');
  }


  getFileStatusObject(row: any) {
    const status = row.fileStatus ? (row.fileStatus as string).trim() : null;
    const statusObj = this.fileStatus.find((obj) => obj.status.includes(status));
    if (statusObj) {
      if (this.hasError(row)) {
        return {
          category: 'Error',
          icon: 'fa-solid fa-octagon-exclamation text-danger',
          status: ['Error'],
        }
      }
      return statusObj;
    } else {
      return null;
    }
  }

  getTooltipContent(row: any): string {
    let tooltip = `Status: ${row.fileStatus}`
    if (this.hasError(row)) {
      tooltip += `\nError Message: ${row.errorMessage}`;
    }
    return tooltip;
}

 hasError(row){
  return row.errorMessage && row.errorMessage.length > 0;
 }

 searchRecord() {
  this.ipcV2.sendAndReceive(IpcChannel.OPEN_URL, environment.domain_login).subscribe(
    (res) => {
      console.log("Opening URL");
    },
    (error) => {
      window.open(environment.domain_login, '_blank');
    }
  );
}

}